import { useCallback, useEffect, useState, useMemo } from 'react';

import { useParams, useLocation } from 'src/router/hooks';
import { request } from 'src/utils/http-request';

import {
  PipelinesTimeseriesData,
  PipelinesTimeseriesDataWithIndex,
} from '../components/list-sidebar/types';
import { parseFilterPathParam } from '../utils/pipeline-filter-query';
import { capturePipelinesExceptionWithTags } from '../utils/sentry';

type Params = {
  repositoryFullSlug?: string;
  branch?: string;
};

export enum PipelinesTimeseriesErrors {
  NoJiraAssociation = 'NO_JIRA_ASSOCIATION',
  LoadFail = 'LOAD_FAIL',
  NoData = 'NO_DATA',
}

export type PipelinesTimeseries = {
  data: PipelinesTimeseriesDataWithIndex[];
  isLoading: boolean;
  error?: PipelinesTimeseriesErrors;
};

// external REST APIs have different naming convetions to internal APIs
// Selectors come from filters queryParams which are formatted for REST APIs
// Pipeline timeseries are internal APIs which require internal selector names
const PIPELINE_SELECTOR_NAME_MAPPINGS = [
  { external: 'BRANCHES', internal: 'BRANCH' },
  { external: 'TAGS', internal: 'TAG' },
  { external: 'PULL-REQUESTS', internal: 'PULLREQUESTS' },
];
const DAY_IN_MS = 1000 * 60 * 60 * 24;
const WEEK_IN_MS = 7 * DAY_IN_MS;
export const PIPLELINES_TIMESERIES_DATA_LENGTH = 8;

export const isJiraIntegrationErrorAbsent = (
  pipelinesTimeseries: PipelinesTimeseries[]
): boolean =>
  pipelinesTimeseries.every(
    ({ error }) => error !== PipelinesTimeseriesErrors.NoJiraAssociation
  );

const formatMinutes = (minutes: number): string => {
  if (!minutes) return '';

  return `${minutes} min${minutes > 1 ? 's' : ''}`;
};

const formatSeconds = (seconds: number): string => {
  if (!seconds) return '';

  return `${seconds}s`;
};

export const formatDuration = (duration?: number): string => {
  if (duration === undefined) return '';

  const minutes = Math.floor(Math.abs(duration) / 60);
  const seconds = Math.floor(Math.abs(duration) % 60);

  const formattedMinutes = formatMinutes(minutes);
  const formattedSeconds = formatSeconds(seconds);

  return `${formattedMinutes} ${formattedSeconds}`.trim() || '0s';
};

export const formatRate = (rate?: number): string => {
  if (rate === undefined) return '';

  const percentage = Math.abs(rate * 100);
  // Rounds to 2 decimal places without displaying decimals for whole numbers
  return `${Math.round(percentage * 100) / 100}%`;
};

const getDateFromStartAt = (startAt: string) => {
  return new Date(startAt).valueOf();
};

const calculateDayDifference = (startAt1: string, startAt2: string) => {
  return Math.round(
    (getDateFromStartAt(startAt1) - getDateFromStartAt(startAt2)) / DAY_IN_MS
  );
};

export const generateEmptyDataItem = (
  data: PipelinesTimeseriesDataWithIndex
): PipelinesTimeseriesDataWithIndex => ({
  ...data,
  data: null,
});

const generateEmptyDataItemForDate = (startAt: string) => ({
  startAt,
  data: null,
});

const generateEmptyData = (itemsToFill: number, startAt: string) => {
  const newData = [];

  for (let i = 1; i <= itemsToFill; i++) {
    const startAtDate = getDateFromStartAt(startAt);
    const weekBeforeStartAt = new Date(
      startAtDate - WEEK_IN_MS * i
    ).toISOString();
    newData.unshift(generateEmptyDataItemForDate(weekBeforeStartAt));
  }

  return newData;
};

const getNumberOfDataItemsToFillAtIndex = (
  data: PipelinesTimeseriesData[],
  index: number
) => {
  const dayDifference = calculateDayDifference(
    data[index].startAt,
    data[index - 1].startAt
  );
  return Math.max(0, Math.round(dayDifference / 7) - 1);
};

const fillDataGaps = (data: PipelinesTimeseriesData[]) => {
  let newData = [...data];

  if (data.length >= 2) {
    for (let i = data.length - 1; i > 0; i--) {
      const itemsToFill = getNumberOfDataItemsToFillAtIndex(data, i);

      newData = [
        ...newData.slice(0, i),
        ...generateEmptyData(itemsToFill, data[i].startAt),
        ...newData.slice(i),
      ];
    }
  }

  return newData;
};

export const padData = (
  data: PipelinesTimeseriesData[],
  padToLength = PIPLELINES_TIMESERIES_DATA_LENGTH
): PipelinesTimeseriesData[] => {
  const dataWithNoGaps = fillDataGaps([...data]);

  const paddedData = generateEmptyData(
    padToLength - dataWithNoGaps.length,
    dataWithNoGaps[0].startAt
  );

  return paddedData.concat(dataWithNoGaps);
};

export const sortData = (
  data: PipelinesTimeseriesData[]
): PipelinesTimeseriesData[] => {
  data.sort((a, b) => {
    return a.startAt.localeCompare(b.startAt);
  });
  return data;
};

const getDataWithIndex = (
  data: PipelinesTimeseriesData[]
): PipelinesTimeseriesDataWithIndex[] =>
  data?.map((item: any, index: number) => ({
    ...item,
    index,
  }));

export const mapPipelineSelectorNames = (unmappedSelector: string): string => {
  let pipelineSelector = unmappedSelector;
  PIPELINE_SELECTOR_NAME_MAPPINGS.forEach(({ external, internal }) => {
    pipelineSelector = pipelineSelector.replace(external, internal);
  });
  return pipelineSelector;
};

export const getFormattedSelectedPipelineType = (pathname: string): string => {
  const { selectedPipelineType } = parseFilterPathParam(pathname);

  if (!selectedPipelineType) return '';

  const [selectorType, selectorPattern] = selectedPipelineType.split(' ');

  if (!selectorPattern) return `${selectorType.toUpperCase()}:`;

  return selectorType.toUpperCase() + selectorPattern;
};

export function usePipelinesTimeseries(
  endpoint: string,
  repositoryUuid?: string | null,
  environmentUuid?: string
): PipelinesTimeseries {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<PipelinesTimeseriesData[]>([]);
  const [error, setError] = useState<PipelinesTimeseriesErrors | undefined>();
  const params = useParams<Params>();
  const location = useLocation();

  const { dataWithIndex } = useMemo(() => {
    return {
      dataWithIndex: getDataWithIndex(data),
    };
  }, [data]);

  const url = useMemo(() => {
    const getPipelineIdQueryParam = () => {
      const selectedPipelineType = mapPipelineSelectorNames(
        getFormattedSelectedPipelineType(location.pathname)
      );

      if (!selectedPipelineType) return '';

      return `?pipelineId=${repositoryUuid}-${selectedPipelineType}-${params.branch}`;
    };

    const getRefNameQueryParam = () =>
      params.branch ? `?refName=${params.branch}` : '';

    const getEnvironmentIdQueryParam = () =>
      environmentUuid ? `?environmentId=${environmentUuid}` : '';

    const getQueryParam = () =>
      getEnvironmentIdQueryParam() ||
      getPipelineIdQueryParam() ||
      getRefNameQueryParam();

    return `/!api/internal/repositories/${
      params.repositoryFullSlug
    }/stats${endpoint}${getQueryParam()}`;
  }, [location, params, repositoryUuid, environmentUuid, endpoint]);

  const getPipelinesTimeseries = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);

    try {
      const response = await request(url);
      const results = response?.aggregated?.results;
      if (!results || results.length === 0) {
        setError(PipelinesTimeseriesErrors.NoData);
      } else {
        setData(padData(sortData(results)));
      }
    } catch (e) {
      if (e.status === 404 && e.message === 'No jira linked repository found') {
        setError(PipelinesTimeseriesErrors.NoJiraAssociation);
      } else {
        setError(PipelinesTimeseriesErrors.LoadFail);
        capturePipelinesExceptionWithTags(e.error ? e.error : e, {
          segment: 'pipelines_load_flaky_sidebar_exception',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    getPipelinesTimeseries();
  }, [getPipelinesTimeseries]);

  return {
    data: dataWithIndex,
    isLoading,
    error,
  };
}
